import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import {
	linkTemplateAttachments,
	unLinkTemplateAttachments,
} from "@/pages/templates/repositories/TemplatesRepositorie";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useTemplateAttachmentGridActionsAndButtons = ({
	gridRef,
	searchTemplateAttachment,
	setShowLinkResourcesToAssetDialog,
	setRemoveAttachmentError,
	setListMessage,
	TemplateId,
}: {
	TemplateId?: number | null;
	newMode?: boolean;
	gridRef: GridApi | null;
	searchTemplateAttachment: Function;
	setRemoveAttachmentError: (err: string) => void | null;
	setListMessage: (err: string) => void | null;
	setShowLinkResourcesToAssetDialog?: Function;
}) => {
	const {
		state: { templateAttachmentsList, listAttachmentSelected },
		actions: {
			setFirstTime,
			setTemplateAttachmentsList,
			setDataAttachmentSelected,
			setTemplateAttachmentToAdd,
			setListAttachmentSelected,
		},
	} = useTemplatesContext();

	const [refreshTrigger, setRefreshTrigger] = useState(1);

	// BUTTONS STATES
	const [addButton, setAddButton] = useState<boolean>(true);
	const [deleteButton, setDeleteButton] = useState<boolean>(false);
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [discardButton, setDiscardButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const isMountRef = useRef(true);
	const [searchParams] = useSearchParams();

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			const updatedList = templateAttachmentsList.filter(
				(item: any) => item.id !== -1,
			);

			setTemplateAttachmentsList(updatedList);
			setFirstTime(true);
			setSaveButton(false);
			setDiscardButton(false);
			setAddButton(true);
			setDeleteButton(false);
			setDeleteOpen(false);
			setDataAttachmentSelected(undefined);
			searchTemplateAttachment();
		}
	}, [searchParams, TemplateId]);

	const saveRow = useCallback(async () => {
		if (!listAttachmentSelected || !listAttachmentSelected[0]?.UploadFileId) {
			setListMessage("Attachment not selected");
		} else {
			const newTemplateAttachment = {
				UploadFileId: listAttachmentSelected[0].UploadFileId,
				FileName: listAttachmentSelected[0].FileName,
				IsAssetResource: false,
			};
			setTemplateAttachmentToAdd(newTemplateAttachment);
			const res = await linkTemplateAttachments(TemplateId as number, [
				newTemplateAttachment,
			]);
			if (res.ok) {
				if (setShowLinkResourcesToAssetDialog) {
					setShowLinkResourcesToAssetDialog(true);
				}
				setRefreshTrigger((prev) => prev + 1);
			} else {
				setListMessage(res.message);
			}
		}

		setListAttachmentSelected(null);
		setAddButton(true);
		setSaveButton(false);
		setDiscardButton(false);
		setDeleteButton(false);
	}, [
		listAttachmentSelected,
		TemplateId,
		linkTemplateAttachments,
		setTemplateAttachmentToAdd,
		setShowLinkResourcesToAssetDialog,
		setRefreshTrigger,
		setListMessage,
		setListAttachmentSelected,
		setAddButton,
		setSaveButton,
		setDiscardButton,
		setDeleteButton,
	]);

	const removeAttachment = useCallback(
		async (attachmentsToRemove) => {
			if (attachmentsToRemove && attachmentsToRemove.length > 0) {
				const formattedAttachments = attachmentsToRemove.map(
					(attachment: any) => ({
						UploadFileId: attachment.UploadFileId,
						IsAssetResource: attachment.IsAssetResource,
					}),
				);

				const res = await unLinkTemplateAttachments(
					TemplateId as number,
					formattedAttachments,
				);
				if (res.ok) {
					refresh();
				} else {
					setRemoveAttachmentError(res.message);
				}
			}

			setDataAttachmentSelected(null);
			setDeleteOpen(false);
			setAddButton(true);
			setSaveButton(false);
			setDiscardButton(false);
			setDeleteButton(false);
			setListAttachmentSelected([]);

			if (gridRef) {
				gridRef.deselectAll();
			}
		},
		[
			TemplateId,
			unLinkTemplateAttachments,
			refresh,
			setRemoveAttachmentError,
			setDataAttachmentSelected,
			setDeleteOpen,
			setAddButton,
			setSaveButton,
			setDiscardButton,
			setDeleteButton,
			setListAttachmentSelected,
			gridRef,
		],
	);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		saveRow,
		removeAttachment,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	};
};
